import { useCallback, useEffect, useMemo } from "react";
import { LinearProgress, Link } from "@mui/material";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Capacitor } from "@capacitor/core";
import { useShallow } from "zustand/shallow";

import AppBar from "components/AppBar";
import AnimatedPlaceholder from "components/AnimatedPlaceholder";
import PageLayout from "components/PageLayout";
import PageLoader from "components/PageLoader";
import AppStatusApi from "api/app-status";
import appVersionIsBigger from "utils/appVersionIsBigger";
import useInterval from "hooks/useInterval";
import useIsOnline from "hooks/useIsOnline";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import lottiePlaceholders from "assets/placeholders";
import { appVersion } from "utils/env";
import googlePlayBadge from "assets/images/google-play-badge.png";
import config from "config";

interface AppStatusWrapperProps {
  children: React.ReactNode;
}

function AppStatusWrapper(props: AppStatusWrapperProps) {
  const { children } = props;
  const appStatus = useBoundStore((state) => state.miscellaneous.appStatus);
  const setAppStatus = useBoundStore((state) => state.miscellaneous.set);
  const isAppFocused = useBoundStore((state) => state.miscellaneous.isAppFocused);
  const { isLoading, storedInLocalStorage } = useBoundStore(
    useShallow((state) => ({
      isLoading: state.appData.isLoading,
      storedInLocalStorage: state.appData.storedInLocalStorage,
    }))
  );
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();
  const isOnline = useIsOnline();
  const { showNotification } = useNotifier();
  const appStatusApi = useMemo(() => new AppStatusApi(showNotification), [showNotification]);
  const platform = Capacitor.getPlatform();

  const fetchAppStatus = useCallback(async () => {
    const { appStatus: latestAppStatus } = await appStatusApi.getAppStatus();

    if (latestAppStatus) {
      setAppStatus({ appStatus: latestAppStatus });
    }
  }, [appStatusApi, setAppStatus]);

  useEffect(() => {
    if (isAppFocused) {
      fetchAppStatus();
    }
  }, [fetchAppStatus, isAppFocused]);

  useInterval(() => fetchAppStatus(), 120000);

  // Update app
  useEffect(() => {
    if (needRefresh && appVersionIsBigger(appStatus?.update.version, appVersion)) {
      updateServiceWorker(true);
    }
  }, [appStatus?.update.version, needRefresh]);

  if (appStatus) {
    if (appStatus.maintenance.active) {
      return (
        <PageLayout appBar={<AppBar backAction={false} noRadius showLogo title='GroupUp' />}>
          <AnimatedPlaceholder animationData={lottiePlaceholders.maintenance} text={appStatus.maintenance.text} />
        </PageLayout>
      );
    }

    if (appVersionIsBigger(appStatus.update.version, appVersion)) {
      return platform === "web" ? (
        <PageLayout appBar={<AppBar backAction={false} noRadius showLogo title='GroupUp' />}>
          <LinearProgress />
          <AnimatedPlaceholder animationData={lottiePlaceholders.update} text={appStatus.update.webAppText} />
        </PageLayout>
      ) : (
        <PageLayout appBar={<AppBar backAction={false} noRadius showLogo title='GroupUp' />}>
          <AnimatedPlaceholder
            action={
              <Link
                style={{ WebkitTapHighlightColor: "transparent" }}
                href={config.links.googlePlay}
                rel='noopener noreferrer'
                sx={{ "&:hover": { opacity: 0.8 }, "&:focus": { opacity: 0.8 } }}
                target='_blank'
              >
                <img width={170} src={googlePlayBadge} alt='Google Play' />
              </Link>
            }
            width='80%'
            animationData={lottiePlaceholders.update}
            text='A significant update has been released. It is necessary to update the app in order to continue using it.'
          />
        </PageLayout>
      );
    }
  }

  if (!isOnline)
    return (
      <>
        <AppBar backAction={false} noRadius showLogo title='GroupUp' />
        <AnimatedPlaceholder
          animationData={lottiePlaceholders.offline}
          text='Check your internet connection'
          width='80%'
        />
      </>
    );

  if (isLoading && !storedInLocalStorage) {
    return (
      <PageLayout appBar={<AppBar backAction={false} noRadius showLogo title='GroupUp' />}>
        <PageLoader text='Synchronize data...' />
      </PageLayout>
    );
  }

  return children;
}

export default AppStatusWrapper;
